import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { SavedActivityInputPreview } from '../lib/followUp';
import {
  StepActivitiesState,
  StepResourceMode,
  StepResourceModeState,
  TemplateStepDraft,
  blankStep,
  getStepResourceMode,
  nextStepOffset,
} from '../lib/mailSequence';

/** Shift per-step records down by one after removing a step. */
const remapIndexedRecord = <T>(
  prev: Record<number, T>,
  removedIndex: number,
): Record<number, T> => {
  const next: Record<number, T> = {};
  Object.entries(prev).forEach(([key, value]) => {
    const oldIndex = Number(key);
    if (oldIndex < removedIndex) next[oldIndex] = value;
    else if (oldIndex > removedIndex) next[oldIndex - 1] = value;
  });
  return next;
};

interface UseSequenceStepsStateOptions {
  initialSteps?: TemplateStepDraft[];
  initialStepActivitiesState?: StepActivitiesState;
  initialStepResourceModes?: StepResourceModeState;
  initialStepSourceTemplateIds?: Record<number, string>;
  initialStepSourceTemplateNames?: Record<number, string>;
  /** Steps below this count cannot be removed. */
  minSteps?: number;
  /** Bump stepsRevision (remounting step editors) on add/remove. */
  remountStepsOnStructureChange?: boolean;
  /** Called before every mutation (e.g. to clear submit/server errors). */
  onMutate?: () => void;
}

export interface SequenceStepsState {
  steps: TemplateStepDraft[];
  setSteps: Dispatch<SetStateAction<TemplateStepDraft[]>>;
  stepsRevision: number;
  bumpStepsRevision: () => void;
  stepActivitiesState: StepActivitiesState;
  setStepActivitiesState: Dispatch<SetStateAction<StepActivitiesState>>;
  stepResourceModes: StepResourceModeState;
  setStepResourceModes: Dispatch<SetStateAction<StepResourceModeState>>;
  stepSourceTemplateIds: Record<number, string>;
  setStepSourceTemplateIds: Dispatch<SetStateAction<Record<number, string>>>;
  stepSourceTemplateNames: Record<number, string>;
  setStepSourceTemplateNames: Dispatch<SetStateAction<Record<number, string>>>;
  setStepActivities: (
    stepIndex: number,
    stepActivities: SavedActivityInputPreview[],
  ) => void;
  setStepResourceMode: (stepIndex: number, mode: StepResourceMode) => void;
  updateStep: (index: number, patch: Partial<TemplateStepDraft>) => void;
  addStep: () => void;
  removeStep: (index: number) => void;
}

/**
 * Owns the sequence step drafts plus the per-step records keyed by step index
 * (activity overrides, resource modes, source template ids/names), keeping
 * those records consistent when steps are added or removed. Shared by the
 * Schedule Mail page and the Mail sequence template editor.
 */
export const useSequenceStepsState = ({
  initialSteps,
  initialStepActivitiesState,
  initialStepResourceModes,
  initialStepSourceTemplateIds,
  initialStepSourceTemplateNames,
  minSteps = 1,
  remountStepsOnStructureChange = false,
  onMutate,
}: UseSequenceStepsStateOptions = {}): SequenceStepsState => {
  const [steps, setSteps] = useState<TemplateStepDraft[]>(
    initialSteps ?? [blankStep(0, 30)],
  );
  const [stepsRevision, setStepsRevision] = useState(0);
  const [stepActivitiesState, setStepActivitiesState] =
    useState<StepActivitiesState>(initialStepActivitiesState ?? {});
  const [stepResourceModes, setStepResourceModes] =
    useState<StepResourceModeState>(initialStepResourceModes ?? {});
  const [stepSourceTemplateIds, setStepSourceTemplateIds] = useState<
    Record<number, string>
  >(initialStepSourceTemplateIds ?? {});
  const [stepSourceTemplateNames, setStepSourceTemplateNames] = useState<
    Record<number, string>
  >(initialStepSourceTemplateNames ?? {});

  const bumpStepsRevision = useCallback(
    () => setStepsRevision((r) => r + 1),
    [],
  );

  const setStepActivities = (
    stepIndex: number,
    stepActivities: SavedActivityInputPreview[],
  ) => {
    onMutate?.();
    setStepActivitiesState((prev) => {
      const mode = getStepResourceMode(stepResourceModes, stepIndex);
      if (stepActivities.length === 0 && mode === 'inherit_mail_one') {
        const { [stepIndex]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [stepIndex]: stepActivities };
    });
  };

  const setStepResourceMode = (stepIndex: number, mode: StepResourceMode) => {
    onMutate?.();
    setStepResourceModes((prev) => {
      if (mode === 'inherit_mail_one') {
        const { [stepIndex]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [stepIndex]: 'custom' };
    });

    if (mode === 'inherit_mail_one') {
      setStepActivitiesState((prev) => {
        const { [stepIndex]: _, ...rest } = prev;
        return rest;
      });
      return;
    }

    setStepActivitiesState((prev) => ({
      ...prev,
      [stepIndex]: prev[stepIndex] ?? [],
    }));
  };

  const updateStep = (index: number, patch: Partial<TemplateStepDraft>) => {
    onMutate?.();
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const addStep = () => {
    onMutate?.();
    setSteps((prev) => [...prev, blankStep(prev.length, nextStepOffset(prev))]);
    if (remountStepsOnStructureChange) bumpStepsRevision();
  };

  const removeStep = (index: number) => {
    if (steps.length <= minSteps) return;
    onMutate?.();
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, sortOrder: i })),
    );
    setStepActivitiesState((prev) => remapIndexedRecord(prev, index));
    setStepResourceModes((prev) => remapIndexedRecord(prev, index));
    setStepSourceTemplateIds((prev) => remapIndexedRecord(prev, index));
    setStepSourceTemplateNames((prev) => remapIndexedRecord(prev, index));
    if (remountStepsOnStructureChange) bumpStepsRevision();
  };

  return {
    steps,
    setSteps,
    stepsRevision,
    bumpStepsRevision,
    stepActivitiesState,
    setStepActivitiesState,
    stepResourceModes,
    setStepResourceModes,
    stepSourceTemplateIds,
    setStepSourceTemplateIds,
    stepSourceTemplateNames,
    setStepSourceTemplateNames,
    setStepActivities,
    setStepResourceMode,
    updateStep,
    addStep,
    removeStep,
  };
};
